import { RankedPromoOfferDiscount } from 'src/apollo/onlineOrdering'

export type LandingConfig = {
  amountMinor?: number
  currency?: string
  promoCode?: string
}

export const parseLandingConfig = (
  encoded: string | null
): LandingConfig | null => {
  if (!encoded) return null
  try {
    const decoded = decodeURIComponent(encoded)
    const parsed = JSON.parse(decoded) as LandingConfig
    return parsed
  } catch {
    return null
  }
}

export const eligibleForLandingUX = (config: LandingConfig | null): boolean => {
  if (!config) return false
  return configContainsOffer(config) || configContainsSavings(config)
}

export const configContainsOffer = (config: LandingConfig) => !!config.promoCode

export const configContainsSavings = (config: LandingConfig) =>
  !!config.amountMinor && !!config.currency

/**
 * Formats a savings amount from the landing config into a localized currency string.
 *
 * @param config - The landing configuration containing amountMinor and currency
 * @param locale - The locale for currency formatting (defaults to 'en-US')
 * @returns A formatted currency string (e.g., "$25.00") or null if no amount
 *
 * @example
 * const formatted = formatSavingsAmount({ amountMinor: 2500, currency: 'USD' }, 'en-US')
 * // Returns "$25.00"
 */
export const formatSavingsAmount = (
  config: LandingConfig,
  locale?: string
): string | null => {
  // note - if savings amount is 0 we're early exiting here as well (intentionally)
  if (!config.amountMinor || !config.currency) {
    return null
  }

  const formatter = new Intl.NumberFormat(locale || 'en-US', {
    style: 'currency',
    currency: config.currency
  })

  const fractionalDigits =
    formatter.resolvedOptions().maximumFractionDigits ?? 2
  const majorAmount = config.amountMinor / Math.pow(10, fractionalDigits)

  return formatter.format(majorAmount)
}

/**
 * Gets a formatted expiration date string for an offer if it expires within 90 days.
 *
 * @param offer - The ranked promo offer discount
 * @param locale - The locale for date formatting (defaults to browser locale)
 * @returns A formatted date string (e.g., "Feb 9, 2025") or null if no expiration within 90 days
 */
export const getOfferExpiryText = (
  offer: RankedPromoOfferDiscount | null | undefined,
  locale?: string
): string | null => {
  if (!offer?.discount?.promoCodes || offer.discount.promoCodes.length === 0) {
    return null
  }

  // Get the first promo code's end date
  const promoCode = offer.discount.promoCodes[0]
  if (!promoCode?.endDate) {
    return null
  }

  const endDate = new Date(promoCode.endDate)
  const now = new Date()

  // Calculate days until expiration
  const millisecondsPerDay = 1000 * 60 * 60 * 24 // ms * seconds * minutes * hours
  const daysUntilExpiry = Math.ceil(
    (endDate.getTime() - now.getTime()) / millisecondsPerDay
  )

  // Only show expiry if it's within 90 days and in the future
  if (daysUntilExpiry <= 0 || daysUntilExpiry > 90) {
    return null
  }

  // Format the date using the provided locale or browser default (SSR-safe)
  const defaultLocale =
    typeof navigator !== 'undefined' ? navigator.language : 'en-US'
  const formatter = new Intl.DateTimeFormat(locale || defaultLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })

  return formatter.format(endDate)
}
